import { z } from 'zod';
import {
  VALID_IAP_MODES,
  IAP_DASHBOARD_MODES,
} from 'components/IAP/ICSConstants';
import {
  formsViewToMode,
  parseFormsViewParam,
  serializeFormsView,
  type FormsView,
} from './iapFormsView';

const formsViewStringSchema = z.union([
  z.literal('working'),
  z.string().regex(/^\d+$/),
]);

const iapParamsStringUrlSchema = z.object({
  mode: z.enum(VALID_IAP_MODES as [string, ...string[]]).optional(),
  phase: z.string().regex(/^\d+$/).optional(),
  formsView: formsViewStringSchema.optional(),
});

export const iapParamsSchema = iapParamsStringUrlSchema.transform((data) => {
  const formsView = parseFormsViewParam(data.formsView ?? null);
  const mode =
    data.mode ??
    (formsView !== undefined
      ? formsViewToMode(formsView)
      : IAP_DASHBOARD_MODES.CURRENT);

  return {
    mode,
    phase: data.phase ? Math.max(0, parseInt(data.phase, 10)) : undefined,
    formsView,
  };
});

export type IapParams = z.infer<typeof iapParamsSchema> & {
  formsView?: FormsView;
};

const { CURRENT } = IAP_DASHBOARD_MODES;

export const DEFAULT_IAP_PARAMS: IapParams = {
  mode: CURRENT,
  phase: 0,
  formsView: undefined,
};

export function validateIapParams(
  searchParams: URLSearchParams
): IapParams | undefined {
  const mode = searchParams.get('mode');
  const phase = searchParams.get('phase');
  const formsView = searchParams.get('formsView');

  if (mode || phase || formsView) {
    const params = {
      mode: mode ?? undefined,
      phase: phase ?? undefined,
      formsView: formsView ?? undefined,
    };

    const parsedParams = iapParamsSchema.safeParse(params);
    if (parsedParams.success) {
      return parsedParams.data;
    }
  }
}

export function validateIapParamsFromUrl(url: string): IapParams | undefined {
  try {
    const urlObj = new URL(url, window.location.origin);
    return validateIapParams(urlObj.searchParams);
  } catch {
    return undefined;
  }
}

export function validateAndCreateIapSearchParams(
  params: Partial<IapParams> & { formsView?: FormsView },
  existingParams?: URLSearchParams
): URLSearchParams {
  const formsView =
    params.formsView ??
    (params.mode === IAP_DASHBOARD_MODES.FUTURE
      ? 'working'
      : params.mode === IAP_DASHBOARD_MODES.CURRENT
        ? undefined
        : undefined);

  const mode =
    params.mode ??
    (formsView !== undefined ? formsViewToMode(formsView) : CURRENT);

  const validationResult = iapParamsSchema.safeParse({
    mode,
    phase: params.phase?.toString(),
    formsView:
      formsView !== undefined ? serializeFormsView(formsView) : undefined,
  });

  if (!validationResult.success) {
    return new URLSearchParams(existingParams || '');
  }

  const newParams = new URLSearchParams(existingParams || '');

  newParams.set('mode', mode);
  if (formsView !== undefined) {
    newParams.set('formsView', serializeFormsView(formsView));
  }
  if (params.phase !== undefined) {
    newParams.set('phase', params.phase.toString());
  }

  return newParams;
}

const INCIDENT_ID =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

export function validateIapRouteStructure(
  pathname: string,
  searchParams: Record<string, string>
) {
  const dashboardSchema = z.object({
    pathname: z.string().regex(new RegExp(`^/iap/${INCIDENT_ID}/dashboard$`)),
    searchParams: z.union([z.object({}), iapParamsStringUrlSchema]),
  });
  const notificationSchema = z.object({
    pathname: z
      .string()
      .regex(new RegExp(`^/iap/${INCIDENT_ID}/notifications$`)),
    searchParams: z
      .object({
        notification: z
          .string()
          .regex(
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
          ),
      })
      .passthrough(),
  });

  return z
    .union([dashboardSchema, notificationSchema])
    .safeParse({ pathname, searchParams });
}
